import * as Sentry from "@sentry/browser";

let isInitialized = false;

export const initSentry = (): void => {
  const dsn = process.env.SENTRY_DSN;

  // Skip Sentry in development (no DSN set)
  if (!dsn) return;

  Sentry.init({
    dsn,
    ignoreErrors: [
      // Browser extension and third-party script errors
      "Non-Error exception captured",
      "Non-Error promise rejection captured",
      // Common browser/extension noise
      "ResizeObserver loop",
      "ResizeObserver loop completed with undelivered notifications",
      // ag-grid legacy React component bug (fixed in ag-grid v27+)
      "Expected subtree parent to be a mounted class component",
    ],
    denyUrls: [
      // Third-party scripts that we don't control
      /widget\.intercom\.io/i,
      /js\.intercomcdn\.com/i,
      /survey\.survicate\.com/i,
      /geoip-js\.com/i,
      /js\.quaderno\.io/i,
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],
    beforeSend(event, hint) {
      // TCV-6856: nothing that identifies a person or a customer may leave the
      // browser. beforeSend is the single egress point, so stripping the user
      // object here holds the guarantee even if something later calls setUser().
      delete event.user;

      const originalException = hint?.originalException;

      // Enrich unhandled Response objects (thrown by generated-sdk runtime)
      // so they show status code + URL instead of just "[object Response]"
      if (
        originalException &&
        typeof originalException === "object" &&
        "status" in originalException &&
        "url" in originalException &&
        typeof (originalException as any).json === "function"
      ) {
        const resp = originalException as unknown as Response;
        event.fingerprint = ["api-error", String(resp.status), resp.url];
        event.tags = {
          ...event.tags,
          apiStatus: String(resp.status),
          apiUrl: resp.url,
        };
        if (event.exception?.values?.[0]) {
          event.exception.values[0].value = `API ${resp.status} error: ${resp.url}`;
        }
      }

      return event;
    },
  });
  isInitialized = true;
};

export const captureException = (
  error: Error | unknown,
  context?: Record<string, unknown>
): void => {
  if (!isInitialized) return;

  if (context) {
    Sentry.withScope((scope) => {
      Object.entries(context).forEach(([key, value]) => {
        scope.setExtra(key, value);
      });
      Sentry.captureException(error);
    });
  } else {
    Sentry.captureException(error);
  }
};
